import { Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, combineLatest, concat, of } from 'rxjs';
import { distinctUntilChanged, map, pairwise, startWith, switchMap } from 'rxjs/operators';
import { timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LucideAngularModule, WifiOff, Wifi } from 'lucide-angular';
import { NetworkStatusService } from 'src/app/core/services/network-status.service';

type BannerState = 'offline' | 'restored' | 'slow' | null;

/**
 * Por quanto tempo o aviso de "conexão lenta" fica visível quando é detectado.
 * O aviso é apenas informativo: aparece por alguns segundos e se esconde
 * sozinho, mesmo que a conexão continue lenta (evita um banner permanente).
 */
const SLOW_BANNER_DURATION_MS = 6000;

@Component({
  selector: 'app-offline-banner',
  standalone: true,
  imports: [AsyncPipe, LucideAngularModule],
  template: `
    @let state = bannerState$ | async;
    @switch (state) {
      @case ('offline') {
        <div class="fixed bottom-0 left-0 right-0 z-[10001] flex items-center justify-center gap-2 bg-[#DC2626] px-4 py-2">
          <lucide-angular [img]="WifiOff" [size]="16" class="text-white shrink-0" />
          <span class="text-sm text-white font-medium">Sem conexão com a internet. Verifique sua rede.</span>
        </div>
      }
      @case ('restored') {
        <div class="fixed bottom-0 left-0 right-0 z-[10001] flex items-center justify-center gap-2 bg-[#16A34A] px-4 py-2">
          <lucide-angular [img]="Wifi" [size]="16" class="text-white shrink-0" />
          <span class="text-sm text-white font-medium">Conexão restaurada.</span>
        </div>
      }
      @case ('slow') {
        <div class="fixed bottom-0 left-0 right-0 z-[10001] flex items-center justify-center gap-2 bg-[#D97706] px-4 py-2">
          <lucide-angular [img]="WifiOff" [size]="16" class="text-white shrink-0" />
          <span class="text-sm text-white font-medium">Conexão lenta detectada. O carregamento pode ser mais demorado.</span>
        </div>
      }
    }
  `,
})
export class OfflineBannerComponent {
  protected readonly WifiOff = WifiOff;
  protected readonly Wifi = Wifi;

  private readonly networkStatus = inject(NetworkStatusService);

  private readonly _showRestored$ = new BehaviorSubject<boolean>(false);

  /**
   * Mostra o aviso de conexão lenta por SLOW_BANNER_DURATION_MS a cada vez que
   * a conexão passa a ser lenta, e depois o esconde automaticamente. Se a
   * conexão oscilar para lenta novamente mais tarde, o aviso reaparece.
   */
  private readonly _showSlow$ = this.networkStatus.isSlowConnection$.pipe(
    distinctUntilChanged(),
    switchMap(slow =>
      slow
        ? concat(of(true), timer(SLOW_BANNER_DURATION_MS).pipe(map(() => false)))
        : of(false)
    ),
  );

  protected readonly bannerState$ = combineLatest([
    this.networkStatus.isOnline$,
    this._showRestored$,
    this._showSlow$,
  ]).pipe(
    map(([online, restored, slow]): BannerState => {
      if (!online) return 'offline';
      if (restored) return 'restored';
      if (slow) return 'slow';
      return null;
    }),
  );

  constructor() {
    this.networkStatus.isOnline$.pipe(
      startWith(null as boolean | null),
      pairwise(),
      switchMap(([prev, curr]) => {
        if (prev === false && curr === true) {
          return concat(of(true), timer(3000).pipe(map(() => false)));
        }
        return of(false);
      }),
      takeUntilDestroyed(),
    ).subscribe(val => this._showRestored$.next(val));
  }
}
